import React, { useState, ChangeEvent, FormEvent } from "react";
import { addDoc, collection, db } from "../services/dbMariaFirestoreCompat";
import { useAuth } from "../contexts/AuthContext";
import { useNavigate } from "react-router-dom";
import { upsertCachedRecord } from "../services/firestoreCache";

// Form type definition
const initialState = {
  model: "",
  year: "",
  size: "",
  reach: "",
  stack: "",
  seatAngleDegrees: "",
  headAngleDegrees: "",
  seatTubeLengthMm: "",
  wheelSizeInches: "",
  rake: "",
  headTubeLengthMm: "",
  chainstayMm: "",
  wheelBase: "",
  bbDrop: "",
  bikeType: "",
  handlebarType: "auto",
  archival: false
};

type FrameForm = typeof initialState;

const AddBikePage: React.FC = () => {
  const { producerInfo } = useAuth();
  const [form, setForm] = useState<FrameForm>(initialState);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const navigate = useNavigate();

  const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    if (type === "checkbox") {
      setForm(f => ({ ...f, [name]: (e.target as HTMLInputElement).checked }));
    } else {
      setForm(f => ({ ...f, [name]: value }));
    }
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      // Required fields validation
      const required = [
        "model", "year", "size", "reach", "stack", "seatAngleDegrees", "headAngleDegrees", "seatTubeLengthMm", "wheelSizeInches", "rake", "headTubeLengthMm", "chainstayMm", "wheelBase", "bbDrop", "bikeType", "handlebarType"
      ];
      for (const field of required) {
        if (!form[field as keyof FrameForm]) throw new Error(`Field ${field} is required.`);
      }
      if (!producerInfo?.brand) throw new Error("Producer brand is missing.");

      // Generate id in format PRODUCER_MODEL_YEAR_SIZE
      const id = [
        producerInfo.brand,
        form.model,
        form.year,
        form.size
      ]
        .map(v => String(v).replace(/\s+/g, "").toUpperCase())
        .join("_");

      // Add bike to database
      const payload = {
        ...form,
        producent: producerInfo.brand,
        certificated: true,
        id
      };
      const docRef = await addDoc(collection(db, "bikeFrames"), payload);
      upsertCachedRecord("bikeFrames", { ...payload, id: docRef.id });
      setForm(initialState);
      navigate("/my-bikes");
    } catch (e) {
      let msg = "Error adding bike.";
      if (typeof e === "string") msg = e;
      else if (e && typeof e === "object" && "message" in e) msg = (e as any).message;
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ background: "#112240", padding: 24, borderRadius: 8, maxWidth: 500, margin: "32px auto", color: "#e6f1ff" }}>
      <h2 style={{ color: "#64ffda" }}>Add New Bike</h2>
      {error && <div style={{ color: "#ff6b6b", marginBottom: 10 }}>{error}</div>}
      <form onSubmit={handleSubmit}>
        <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
          <label>
            Model
            <span style={{ color: "#8892b0", fontSize: 13, display: "block" }}>Bike model name (e.g. Specialissima RC)</span>
            <input name="model" placeholder="Model" value={form.model} onChange={handleChange} required style={{ width: "100%" }} />
          </label>
          <label>
            Year
            <span style={{ color: "#8892b0", fontSize: 13, display: "block" }}>Production year (e.g. 2024)</span>
            <input name="year" placeholder="Year" value={form.year} onChange={handleChange} required style={{ width: "100%" }} />
          </label>
          <label>
            Size
            <span style={{ color: "#8892b0", fontSize: 13, display: "block" }}>Frame size (e.g. XS, S, M, L, XL)</span>
            <input name="size" placeholder="Size" value={form.size} onChange={handleChange} required style={{ width: "100%" }} />
          </label>
          <label>
            Bike type
            <span style={{ color: "#8892b0", fontSize: 13, display: "block" }}>Type of bike (e.g. Road, MTB, Gravel)</span>
            <input name="bikeType" placeholder="Bike type" value={form.bikeType} onChange={handleChange} required style={{ width: "100%" }} />
          </label>
          <label>
            Handlebar type
            <span style={{ color: "#8892b0", fontSize: 13, display: "block" }}>Type of handlebar (e.g. drop, flat, auto)</span>
            <input name="handlebarType" placeholder="Handlebar type" value={form.handlebarType} onChange={handleChange} required style={{ width: "100%" }} />
          </label>
          <label>
            Reach (mm)
            <span style={{ color: "#8892b0", fontSize: 13, display: "block" }}>Horizontal distance from bottom bracket to top center of head tube</span>
            <input name="reach" placeholder="Reach (mm)" value={form.reach} onChange={handleChange} required style={{ width: "100%" }} />
          </label>
          <label>
            Stack (mm)
            <span style={{ color: "#8892b0", fontSize: 13, display: "block" }}>Vertical distance from bottom bracket to top center of head tube</span>
            <input name="stack" placeholder="Stack (mm)" value={form.stack} onChange={handleChange} required style={{ width: "100%" }} />
          </label>
          <label>
            Seat tube angle (°)
            <span style={{ color: "#8892b0", fontSize: 13, display: "block" }}>Angle of the seat tube in degrees</span>
            <input name="seatAngleDegrees" placeholder="Seat tube angle (°)" value={form.seatAngleDegrees} onChange={handleChange} required style={{ width: "100%" }} />
          </label>
          <label>
            Head tube angle (°)
            <span style={{ color: "#8892b0", fontSize: 13, display: "block" }}>Angle of the head tube in degrees</span>
            <input name="headAngleDegrees" placeholder="Head tube angle (°)" value={form.headAngleDegrees} onChange={handleChange} required style={{ width: "100%" }} />
          </label>
          <label>
            Seat tube length (mm)
            <span style={{ color: "#8892b0", fontSize: 13, display: "block" }}>Length of the seat tube in millimeters</span>
            <input name="seatTubeLengthMm" placeholder="Seat tube length (mm)" value={form.seatTubeLengthMm} onChange={handleChange} required style={{ width: "100%" }} />
          </label>
          <label>
            Wheel size (inches)
            <span style={{ color: "#8892b0", fontSize: 13, display: "block" }}>Diameter of the wheel in inches</span>
            <input name="wheelSizeInches" placeholder="Wheel size (inches)" value={form.wheelSizeInches} onChange={handleChange} required style={{ width: "100%" }} />
          </label>
          <label>
            Rake (mm)
            <span style={{ color: "#8892b0", fontSize: 13, display: "block" }}>Fork offset in millimeters</span>
            <input name="rake" placeholder="Rake (mm)" value={form.rake} onChange={handleChange} required style={{ width: "100%" }} />
          </label>
          <label>
            Head tube length (mm)
            <span style={{ color: "#8892b0", fontSize: 13, display: "block" }}>Length of the head tube in millimeters</span>
            <input name="headTubeLengthMm" placeholder="Head tube length (mm)" value={form.headTubeLengthMm} onChange={handleChange} required style={{ width: "100%" }} />
          </label>
          <label>
            Chainstay (mm)
            <span style={{ color: "#8892b0", fontSize: 13, display: "block" }}>Length of the chainstay in millimeters</span>
            <input name="chainstayMm" placeholder="Chainstay (mm)" value={form.chainstayMm} onChange={handleChange} required style={{ width: "100%" }} />
          </label>
          <label>
            BB drop (mm)
            <span style={{ color: "#8892b0", fontSize: 13, display: "block" }}>Bottom bracket drop in millimeters</span>
            <input name="bbDrop" placeholder="BB drop (mm)" value={form.bbDrop} onChange={handleChange} required style={{ width: "100%" }} />
          </label>
          <label>
            Wheelbase (mm)
            <span style={{ color: "#8892b0", fontSize: 13, display: "block" }}>Distance between front and rear wheel axles</span>
            <input name="wheelBase" placeholder="Wheelbase (mm)" value={form.wheelBase} onChange={handleChange} required style={{ width: "100%" }} />
          </label>
          <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <input type="checkbox" name="archival" checked={form.archival} onChange={handleChange} />
            Archival
            <span style={{ color: "#8892b0", fontSize: 13 }}>(Mark as archival bike)</span>
          </label>
        </div>
        <div style={{ marginTop: 24, display: "flex", gap: 10, justifyContent: "flex-end" }}>
          <button type="button" onClick={() => navigate("/my-bikes")}
            style={{ background: "#233554", color: "#fff", border: "none", borderRadius: 4, padding: "8px 18px", cursor: "pointer" }}>
            Cancel
          </button>
          <button type="submit" disabled={loading}
            style={{ background: "#64ffda", color: "#0a192f", border: "none", borderRadius: 4, padding: "8px 18px", cursor: loading ? "not-allowed" : "pointer", fontWeight: "bold" }}>
            {loading ? "Adding..." : "Add bike"}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AddBikePage;
